import { QueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { PagedResponse } from "../models/responses/pagedResponse";
import { actAsCacheKey } from "../../contexts/actAsStore";
import {
  isLogoDetailQueryKey,
  logoDetailKey,
  logoQueryKeyScope,
} from "./logoDetailCache";

// "Unassign customer" in the logo popup (flag REACT_APP_LOGO_UNASSIGN_ENABLED):
// the cache side of the ONE DELETE. Kept in a leaf module next to
// logoDetailCache for the same reason (no import cycle with the action
// handler / repository). Nothing here fetches.

/**
 * The logo without its customer link - the exact shape mapLogo produces for
 * a logo that has no customer/vendor org (global logos): both fields
 * undefined. The entity types them as required, hence the cast; the list
 * rows and the popup already render such logos (`logo.customer &&`).
 *
 * The API's truth after the refetch is `customer` = the vendor org (type
 * "vendor"): LogoDto.Customer is mapped from CustomerOrVendor and
 * LogoDto.Vendor is never filled, so a row that HAS a customer carries no
 * vendor org to fall back to - it is unknown locally (name included) and
 * cannot be patched in. Until the list's one GET lands the row is
 * therefore in the global-logo form: no org named, neither Assign nor
 * Unassign offered; getLogoOrgId falls back to mainOrganizationId, which
 * is why the next open still loads through a readable org.
 */
export function withoutCustomer(logo: BaseLogo): BaseLogo {
  return {
    ...logo,
    customer: undefined,
    customerId: undefined,
  } as unknown as BaseLogo;
}

function isPagedLogos(data: unknown): data is PagedResponse<BaseLogo> {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as PagedResponse<BaseLogo>).results)
  );
}

function isInfiniteLogos(
  data: unknown,
): data is { pages: PagedResponse<BaseLogo>[] } {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

function patchResults(
  results: BaseLogo[] | undefined,
  logoId: number,
): BaseLogo[] | undefined {
  if (!Array.isArray(results)) return results;
  let changed = false;
  const next = results.map((row) => {
    if (!row || row.id !== logoId) return row;
    if (row.customer === undefined && row.customerId === undefined) return row;
    changed = true;
    return withoutCustomer(row);
  });
  return changed ? next : results;
}

/**
 * A cached list entry with the customer dropped from the row of the given
 * id: a paged list ({results}) or an infinite list ({pages: [{results}]}).
 * Any other shape, or nothing to change, comes back as the very same
 * reference. Exported for tests.
 */
export function patchCachedLogoRowsUnassigned(
  data: unknown,
  logoId: number,
): unknown {
  if (!data) return data;
  if (isPagedLogos(data)) {
    const results = patchResults(data.results, logoId);
    return results === data.results ? data : { ...data, results };
  }
  if (isInfiniteLogos(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      if (!isPagedLogos(page)) return page;
      const results = patchResults(page.results, logoId);
      if (results === page.results) return page;
      changed = true;
      return { ...page, results };
    });
    return changed ? { ...data, pages } : data;
  }
  return data;
}

/**
 * The detail entry (["logos", actAs, id]) handed back to the popup as a
 * list stub - `mapped` applied, detailFromList set, updatedAt 0 - so the
 * NEXT open loads it with the popup's one GetLogo (a stub is re-seeded from
 * the fresh row under popup-fast, and an updatedAt-0 entry is loaded on
 * mount without it) instead of showing this entry as the truth. A GetLogo
 * of the entry still in flight (the popup's own background load, when the
 * click came before it landed) is dropped first: it went out through the
 * customer org BEFORE the DELETE, and its landing would otherwise replace
 * the stub with a "real" detail that still carries the customer. query-core
 * cancels synchronously (the retryer rejects -> the query reverts to its
 * pre-fetch state in the same tick), so the write below always wins. A
 * missing entry is not created. Returns whether an entry was written.
 */
function writeDetailAsStub(
  queryClient: QueryClient,
  logoId: number,
  mapped: (detail: BaseLogo) => BaseLogo,
): boolean {
  const detailKey = logoDetailKey(logoId);
  if (queryClient.getQueryState(detailKey)?.fetchStatus === "fetching") {
    void queryClient.cancelQueries({ queryKey: detailKey, exact: true });
  }
  const detail = queryClient.getQueryData<BaseLogo>(detailKey);
  if (!detail) return false;
  queryClient.setQueryData<BaseLogo>(
    detailKey,
    { ...mapped(detail), detailFromList: true },
    { updatedAt: 0 },
  );
  return true;
}

/**
 * After the DELETE answered 404 (the API cannot see the link: removed
 * meanwhile, or one the sub-flag cannot classify): the popup's customer is
 * stale but whether it is gone is unknown, so the detail entry keeps it and
 * is only handed back as a stub (see writeDetailAsStub) - the fresh row of
 * the list refetch decides what the next open shows, and that open costs
 * the ONE GetLogo the popup always makes. Without this the entry stayed a
 * real, fresh detail: the next open showed the removed customer with 0
 * calls and offered "Unassign customer" again (skeptic round 1). 0 calls,
 * nothing created. Returns whether an entry was written.
 */
export function downgradeLogoDetailToStub(
  queryClient: QueryClient,
  logoId: number,
): boolean {
  return writeDetailAsStub(queryClient, logoId, (detail) => detail);
}

/**
 * After the DELETE answered 204: drop the customer from the logo everywhere
 * the CURRENT act-as scope has it cached - 0 calls, pure cache write.
 *
 * - The detail entry (["logos", actAs, id]) loses the customer and is
 *   handed back to the popup as a list stub (writeDetailAsStub: the next
 *   open fires the popup's ONE background GetLogo through the fresh row's
 *   org, because the customer org can no longer read the logo; an in-flight
 *   GetLogo through the customer org is dropped). Nothing is fetched here,
 *   and a missing entry is not created.
 * - Every LIST entry of the scope (paged lists of the logo list screen,
 *   infinite lists of the design picker) gets the row patched, so the row
 *   is right the moment the popup closes, before the refetch lands.
 *
 * Never written: another act-as scope's entries, the design picker's
 * unscoped entry, entries that would not change (no dispatch). Returns the
 * number of entries written.
 */
export function patchLogoCustomerUnassigned(
  queryClient: QueryClient,
  logoId: number,
): number {
  const scope = actAsCacheKey();
  let written = 0;
  if (writeDetailAsStub(queryClient, logoId, withoutCustomer)) written += 1;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["logos"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (isLogoDetailQueryKey(key)) return;
      if (logoQueryKeyScope(key) !== scope) return;
      const current = query.state.data;
      if (current == null) return;
      const next = patchCachedLogoRowsUnassigned(current, logoId);
      if (next === current) return;
      queryClient.setQueryData(key, next);
      written += 1;
    });
  return written;
}

/**
 * The refetch after an unassign (204, or a 404 that says the link is gone
 * already): every cached logo LIST under ["logos"] is invalidated - the one
 * on screen refetches (1 GET, the same cost "Assign customer" pays today),
 * the others on their next mount - and so is the customer tab's
 * ["logo-links", customerId] entry (0 calls while the tab is not on
 * screen). Detail entries are left out on purpose: the popup's own entry
 * was handed back as a stub above (patched, or downgraded after a 404), and
 * a refetch of it through the customer org would answer 404 now.
 */
export function invalidateAfterLogoUnassign(
  queryClient: QueryClient,
  customerId: number,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["logos"],
      predicate: (query) => !isLogoDetailQueryKey(query.queryKey),
    }),
    queryClient.invalidateQueries({ queryKey: ["logo-links", customerId] }),
  ]).then(() => undefined);
}
